import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LogoBadge, Wordmark } from "./Logo";
import { scrollToId } from "@/data/content";

const LINKS = [
  { label: "Products", id: "products", testid: "cbdoodle-nav-products" },
  { label: "Benefits", id: "benefits", testid: "cbdoodle-nav-benefits" },
  { label: "Why Doodles?", id: "why-poodles", testid: "cbdoodle-nav-why" },
  { label: "Dosing", id: "dosing", testid: "cbdoodle-nav-dosing" },
  { label: "Quality", id: "quality", testid: "cbdoodle-nav-quality" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="cbdoodle-header-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-[#E6E0D4] bg-[#FAF7F2]/85 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <button
          data-testid="cbdoodle-logo-link"
          onClick={() => scrollToId("hero")}
          className="flex items-center gap-2.5"
        >
          <LogoBadge size={42} />
          <Wordmark />
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="relative font-sans text-sm text-[#5C5850] transition-colors hover:text-[#2C2B29] after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#C58F3B] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="cbdoodle-header-cta-button"
            onClick={() => scrollToId("waitlist")}
            className="hidden rounded-full bg-[#C58F3B] px-5 py-2.5 font-sans text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-[#B07D2B] hover:shadow-md active:scale-[0.98] sm:inline-block"
          >
            Get Early Access
          </button>
          <button
            data-testid="cbdoodle-mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full p-2 text-[#2C2B29] lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-[#E6E0D4] bg-[#FAF7F2]/95 backdrop-blur-md lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="rounded-lg px-3 py-3 text-left font-sans text-[15px] text-[#2C2B29] hover:bg-[#F3EDE2]"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("waitlist")}
                className="mt-2 rounded-full bg-[#C58F3B] px-5 py-3 font-sans text-sm font-medium text-white"
              >
                Get Early Access
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
